import "./lib/fontawesome";
import { createRoot, hydrateRoot } from "react-dom/client";
import { HelmetProvider } from "react-helmet-async";
import { BrowserRouter } from "react-router-dom";
import "@fontsource-variable/inter/index.css";
import "@fontsource-variable/space-grotesk/index.css";
import { AppTree } from "./App.tsx";
import { loadThirdPartyScripts } from "./lib/thirdPartyScripts";
import { isStaticRoute, normalizeRoutePath } from "./lib/ssgRoutes";
import "./index.css";

const container = document.getElementById("root")!;
const tree = (
  <HelmetProvider>
    <BrowserRouter>
      <AppTree />
    </BrowserRouter>
  </HelmetProvider>
);

// Hydration is only valid when the SSG build produced this exact document:
//  1. the explicit marker written by scripts/ssg.ts is present,
//  2. #root contains real React element markup (a bare <!--app-html--> comment
//     or whitespace is NOT hydratable, while hasChildNodes() would be true),
//  3. the markup was generated for the route we are currently on,
//  4. that route is part of the generated SSG route list.
const currentPath = normalizeRoutePath(window.location.pathname);
const prerenderedRoute = container.getAttribute("data-prerendered-route");
const checks = {
  hasMarker: container.getAttribute("data-prerendered") === "true",
  hasReactMarkup: container.childElementCount > 0,
  routeMatches: !!prerenderedRoute && normalizeRoutePath(prerenderedRoute) === currentPath,
  routeIsGenerated: isStaticRoute(currentPath),
};
const shouldHydrate = Object.values(checks).every(Boolean);

if (shouldHydrate) {
  hydrateRoot(container, tree, {
    onRecoverableError: (err, info) => {
      // eslint-disable-next-line no-console
      console.error("[hydration]", err, "\nComponent stack:", info?.componentStack);
    },
  });
} else {
  if (import.meta.env.DEV) {
    const failed = Object.entries(checks)
      .filter(([, pass]) => !pass)
      .map(([name]) => name);
    // eslint-disable-next-line no-console
    console.info(`[render] createRoot for ${currentPath} — failed checks: ${failed.join(", ")}`);
  }
  // Any stale / non-hydratable markup must not be reused.
  if (container.hasChildNodes()) container.innerHTML = "";
  createRoot(container).render(tree);
}

loadThirdPartyScripts();
